import { BookOpen, Target, Users, FileText, MessagesSquare, Zap, Briefcase, ListChecks, Inbox } from 'lucide-react';
import type { GlyphName } from '../components/Glyph';

export type Role = 'beginners' | 'learners' | 'parents' | 'daily' | 'creative' | 'work' | 'jobs' | 'agents';

export interface Lesson {
  slug: string;
  title: string;
  duration: number;
  summary: string;
  content: { type: 'concept' | 'example' | 'do' | 'reflect'; title?: string; body: string; prompt?: string }[];
}

export interface Module {
  slug: string;
  title: string;
  description: string;
  output: string;
  lessons: Lesson[];
}

export interface SkillPath {
  slug: Role;
  title: string;
  tagline: string;
  description: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  totalMinutes: number;
  outcomes: string[];
  glyph: GlyphName;
  accent: string;
  modules: Module[];
  proOnly?: boolean;
}

const beginnersModules: Module[] = [
  {
    slug: 'first-steps',
    title: 'Your first hour with AI',
    description: 'A gentle introduction. No jargon, no assumed background.',
    output: 'A clear mental model of what AI can and cannot do.',
    lessons: [
      {
        slug: 'what-ai-actually-is',
        title: 'What AI actually is (and is not)',
        duration: 7,
        summary: 'A working mental model for what general-purpose AI does, in plain language.',
        content: [
          { type: 'concept', title: 'A very smart autocomplete', body: 'A modern AI model is, at heart, a system that predicts the next word given everything it has seen so far. It is not a search engine, a database, or a person. It generates plausible text in the moment.' },
          { type: 'concept', title: 'Why this matters', body: 'Because the model is generating, not retrieving, it can sound confident and still be wrong. That is not a bug — it is how the system works. Your job as a user is to learn when to trust it and how to check.' },
          { type: 'reflect', body: 'Think of one question you asked an AI recently. Was it the kind of question that needs a fact, or the kind that needs an idea? Which is the model better at?' },
        ],
      },
      {
        slug: 'why-prompts-matter',
        title: 'Why the way you ask matters',
        duration: 6,
        summary: 'The same model gives wildly different answers depending on how the question is framed.',
        content: [
          { type: 'concept', body: 'Every word in your message steers the prediction. Vague messages get vague answers. Specific roles, examples, and constraints get specific work.' },
          { type: 'do', title: 'Try the difference yourself', body: 'Open the workspace. First ask: "Tell me about exercise." Then ask: "I am a 40-year-old beginner who works at a desk. Suggest a 20-minute home workout I can do three times a week with no equipment." Compare the two answers.', prompt: 'I am a 40-year-old beginner who works at a desk. Suggest a 20-minute home workout I can do three times a week with no equipment.' },
        ],
      },
    ],
  },
  {
    slug: 'spotting-mistakes',
    title: 'Spotting mistakes and hallucinations',
    description: 'The single most important AI skill: knowing when not to trust the answer.',
    output: 'A verification habit you can apply to any AI tool.',
    lessons: [
      {
        slug: 'why-ai-makes-things-up',
        title: 'Why AI confidently makes things up',
        duration: 8,
        summary: 'A short tour of hallucinations and the patterns that produce them.',
        content: [
          { type: 'concept', body: 'When the model has a gap in what it knows, it does not say "I do not know." It produces the most plausible-sounding completion. That completion can be entirely invented.' },
          { type: 'example', body: 'A model asked for a quote from a real book may invent a sentence that sounds exactly like that author. The cadence is right. The sentence is fake.' },
          { type: 'do', body: 'Pick a topic you know well. Ask the model a moderately specific question. Read the answer with a skeptic eye. Where might it be inventing?' },
        ],
      },
      {
        slug: 'verification-checklist',
        title: 'A two-minute verification checklist',
        duration: 6,
        summary: 'Five quick checks that catch most AI mistakes before they become your mistakes.',
        content: [
          { type: 'concept', body: 'Check: 1) Are the names and dates real? 2) Could you cite a source? 3) Does the math add up? 4) Is the model contradicting itself? 5) Would an expert in this field agree?' },
          { type: 'reflect', body: 'Which of those five checks is hardest for you to do? That is the one to practice.' },
        ],
      },
    ],
  },
  {
    slug: 'first-real-use',
    title: 'Your first real use case',
    description: 'A small, concrete task you can complete with AI today.',
    output: 'One useful thing made better by AI.',
    lessons: [
      {
        slug: 'small-task-rewrite',
        title: 'Rewrite something hard to say',
        duration: 8,
        summary: 'A short polite email, a tricky message, a note that has been sitting in drafts for a week.',
        content: [
          { type: 'do', body: 'Pick a real message you have been putting off writing. Drop your rough draft into the workspace and ask the model to make it warmer, shorter, or more direct.', prompt: 'Here is a draft message I am nervous to send. Rewrite it so it is warm, clear, and respectful of the other person\'s time. Keep it under 120 words.\n\nDraft:\n[paste]' },
          { type: 'reflect', body: 'How did the AI version compare to your draft? What did you keep, and what did you change back?' },
        ],
      },
    ],
  },
];

const learnersModules: Module[] = [
  {
    slug: 'study-honestly',
    title: 'Studying honestly with AI',
    description: 'Use AI to learn faster without learning less.',
    output: 'A study habit that builds real understanding.',
    lessons: [
      {
        slug: 'tutor-not-answer-machine',
        title: 'Treat AI like a tutor, not an answer machine',
        duration: 8,
        summary: 'Why asking for the answer is the worst use of AI for studying.',
        content: [
          { type: 'concept', body: 'If you ask for the answer, you stop thinking. If you ask the model to teach the concept, quiz you, or explain where you went wrong, you keep thinking. The first option saves time today and costs you the test.' },
          { type: 'do', body: 'Try the tutor pattern.', prompt: 'You are a patient tutor. Do not give me the answer. Instead, walk me through the concept of [topic] step by step. After each step, ask me a small question to check my understanding before moving on.' },
        ],
      },
      {
        slug: 'feynman-with-ai',
        title: 'Teach it back: the Feynman method with AI',
        duration: 7,
        summary: 'The fastest way to find the gaps in your understanding.',
        content: [
          { type: 'concept', body: 'Explain a topic in your own words. Hand the explanation to the model and ask it to find the gaps and ask the questions a confused student would ask.' },
          { type: 'do', body: 'Pick a topic from your current class.', prompt: 'I am going to explain [topic] in my own words. Read my explanation. Identify the parts that are unclear or missing, then ask me three questions a confused classmate might ask.\n\nMy explanation:\n[paste]' },
        ],
      },
    ],
  },
  {
    slug: 'reading-and-summarizing',
    title: 'Reading and summarizing better',
    description: 'Get more out of a chapter, an article, or a paper.',
    output: 'A repeatable summary workflow.',
    lessons: [
      {
        slug: 'chapter-to-notes',
        title: 'Turn a chapter into study notes',
        duration: 10,
        summary: 'A reliable prompt that produces real notes, not vague summaries.',
        content: [
          { type: 'do', body: 'Paste a long passage and run this prompt.', prompt: 'You are a study coach. Below is a chapter from a textbook. Produce: 1) the three most important ideas, in one sentence each, 2) five terms a student should be able to define, 3) two questions an exam might ask, 4) one common misconception to watch out for.\n\nChapter:\n[paste]' },
        ],
      },
    ],
  },
  {
    slug: 'interview-prep',
    title: 'Interview and presentation prep',
    description: 'Sparring with AI before the real conversation.',
    output: 'A confident answer to a hard question.',
    lessons: [
      {
        slug: 'mock-interview',
        title: 'Run a tough mock interview',
        duration: 12,
        summary: 'AI is uniquely good at being a relentless interviewer.',
        content: [
          { type: 'do', body: 'Use the model as a critical interviewer.', prompt: 'You are a skeptical interviewer for a [role] position. Ask me one tough question at a time. After my answer, give specific feedback on what was strong, what was vague, and what to add. Then ask the next question.' },
          { type: 'reflect', body: 'Which question caught you off guard? That is the one to practice for.' },
        ],
      },
    ],
  },
];

const parentsModules: Module[] = [
  {
    slug: 'parent-foundations',
    title: 'Foundations for parents',
    description: 'What every parent should know before their kid touches AI.',
    output: 'A clear, calm point of view to bring to family conversations.',
    lessons: [
      {
        slug: 'five-conversations',
        title: 'The five conversations to have first',
        duration: 8,
        summary: 'Short, concrete topics to cover before any kid uses general-purpose AI.',
        content: [
          { type: 'concept', body: 'Where the answer comes from. How to know if it is wrong. What the AI is allowed to know about you. Why something can sound right but be wrong. What you should not ask AI to do for you.' },
          { type: 'reflect', body: 'Which of these five have you actually had with your kid? Pick the missing one and have it this week.' },
        ],
      },
      {
        slug: 'reading-a-transcript',
        title: 'How to read a transcript well',
        duration: 7,
        summary: 'What to look for on the guardian dashboard, and what to ignore.',
        content: [
          { type: 'concept', body: 'Look for: refusals, unexpected topics, moments where your kid pushed back on the model. Ignore: minor typos, repeated activities, perfect-looking answers. The interesting parts are the friction.' },
        ],
      },
    ],
  },
  {
    slug: 'coaching-a-kid',
    title: 'Coaching a kid through an activity',
    description: 'How to sit beside, not on top of, a kid using AI.',
    output: 'A small set of phrases that turn AI use into a learning moment.',
    lessons: [
      {
        slug: 'three-good-questions',
        title: 'Three questions that turn an activity into a lesson',
        duration: 6,
        summary: 'The right questions, asked at the right moment, do most of the teaching.',
        content: [
          { type: 'concept', body: 'After an answer: "How would you check that?" During a refusal: "Why do you think the model said no?" After the activity: "What is one thing you would ask differently next time?"' },
        ],
      },
    ],
  },
  {
    slug: 'family-rhythm',
    title: 'Setting a healthy family rhythm',
    description: 'Time caps, quiet hours, and the once-a-week dashboard check.',
    output: 'A weekly rhythm that builds trust without becoming surveillance.',
    lessons: [
      {
        slug: 'weekly-rhythm',
        title: 'A weekly rhythm that works',
        duration: 6,
        summary: 'Once a week, five minutes, one question per kid.',
        content: [
          { type: 'concept', body: 'Open the dashboard once a week. Pick one transcript per kid. Ask one question at dinner. That is the entire job. More than that becomes surveillance, and surveillance kills the conversation.' },
        ],
      },
    ],
  },
];

const dailyModules: Module[] = [
  {
    slug: 'inbox-and-messages',
    title: 'Inbox and messages',
    description: 'Stop staring at the blinking cursor on the hard email.',
    output: 'A faster, kinder way through your inbox.',
    lessons: [
      {
        slug: 'polite-decline',
        title: 'Write a polite decline',
        duration: 6,
        summary: 'A short, warm no that does not burn the relationship.',
        content: [
          { type: 'do', body: 'Use the workspace.', prompt: 'I need to politely decline [the request]. Write a short, warm message that says no clearly, thanks them for thinking of me, and leaves the door open for the future. Keep it under 90 words.' },
        ],
      },
      {
        slug: 'inbox-triage',
        title: 'Triage a long thread',
        duration: 7,
        summary: 'Make sense of a 40-message email chain in two minutes.',
        content: [
          { type: 'do', body: 'Paste the thread.', prompt: 'Below is a long email thread. Tell me: 1) the current status, 2) what each person wants, 3) what decision is pending, 4) what I should do next.\n\nThread:\n[paste]' },
        ],
      },
    ],
  },
  {
    slug: 'planning-and-life',
    title: 'Planning and life admin',
    description: 'Small AI moves that take real friction out of a normal week.',
    output: 'A plan you might actually follow.',
    lessons: [
      {
        slug: 'meal-planning',
        title: 'Plan a week of meals around what you already have',
        duration: 8,
        summary: 'AI is unreasonably good at this and almost nobody uses it for it.',
        content: [
          { type: 'do', body: 'Run the prompt.', prompt: 'Here is what is in my pantry and fridge: [list]. Plan five dinners for a household of [number] that use these ingredients first. Keep prep time under 30 minutes per meal. Include a small shopping list of anything missing.' },
        ],
      },
      {
        slug: 'unstuck-on-a-decision',
        title: 'Get unstuck on a small decision',
        duration: 6,
        summary: 'Use AI as a sparring partner, not an oracle.',
        content: [
          { type: 'concept', body: 'For most small decisions, you do not need an answer — you need to think more clearly. AI is excellent at that role.' },
          { type: 'do', body: 'Try the sparring prompt.', prompt: 'I am trying to decide between [option A] and [option B]. Ask me five sharp questions that would help me think more clearly, one at a time. Do not tell me what you think.' },
        ],
      },
    ],
  },
];

const creativeModules: Module[] = [
  {
    slug: 'brainstorm-honestly',
    title: 'Brainstorming that does not collapse into mush',
    description: 'How to use AI for ideas without diluting your taste.',
    output: 'A short list of usable ideas, not 50 generic ones.',
    lessons: [
      {
        slug: 'wide-then-narrow',
        title: 'Wide first, narrow second',
        duration: 8,
        summary: 'AI is good at quantity. You are good at picking. Use the right tool at the right step.',
        content: [
          { type: 'concept', body: 'Ask the model for 30 ideas, knowing 27 will be junk. Then ask it to pressure-test the three you liked and improve them. Quantity from the model, judgement from you.' },
          { type: 'do', body: 'Try the pattern.', prompt: 'Generate 30 distinct ideas for [topic]. Make them varied in tone and angle. After the list, ask me which three are most interesting so we can develop them further.' },
        ],
      },
    ],
  },
  {
    slug: 'rewriting-and-titles',
    title: 'Rewriting headlines and openings',
    description: 'The difference between a piece that gets read and one that does not.',
    output: 'A first paragraph or headline that earns the next sentence.',
    lessons: [
      {
        slug: 'rewrite-headline',
        title: 'Rewrite a headline ten ways',
        duration: 6,
        summary: 'Quantity surfaces options. You pick the one you would not have thought of.',
        content: [
          { type: 'do', body: 'Run the prompt.', prompt: 'Here is my draft headline: "[paste]". Rewrite it ten ways across different angles: curiosity, contrarian, specific, story-led, blunt. Mark the two you would pick and explain why.' },
        ],
      },
    ],
  },
  {
    slug: 'pressure-testing',
    title: 'Pressure-testing your own ideas',
    description: 'Use AI as the contrarian in the room.',
    output: 'An idea that has survived its first real critique.',
    lessons: [
      {
        slug: 'attack-the-idea',
        title: 'Have AI attack your idea',
        duration: 7,
        summary: 'The most underused prompt pattern in creative work.',
        content: [
          { type: 'do', body: 'Run the prompt.', prompt: 'I am working on [idea]. You are a thoughtful, skeptical reader. Attack it. Where is it cliched? Where am I assuming the reader cares more than they do? What is the strongest counter-argument? End with one suggestion that would meaningfully improve it.' },
        ],
      },
    ],
  },
];

const workModules: Module[] = [
  {
    slug: 'meetings-and-notes',
    title: 'Meetings and notes that earn their keep',
    description: 'Turn every meeting into a clear note your team can actually act on.',
    output: 'A repeatable shape for meeting prep, recap, and follow-up.',
    lessons: [
      {
        slug: 'pre-meeting-brief',
        title: 'A two-minute pre-meeting brief',
        duration: 8,
        summary: 'Walk in with the question, the decision needed, and the failure modes already mapped.',
        content: [
          { type: 'concept', body: 'Most meetings start blurry because the prep was blurry. A short brief — purpose, decision, options, risks — tightens the room before anyone speaks.' },
          { type: 'do', title: 'Have AI draft the brief', body: 'Paste your invite, agenda, and last status into the model. Ask for: the single decision needed, three options with tradeoffs, the two risks worth surfacing.', prompt: 'Read this meeting context and write a two-minute brief: the single decision, three options with tradeoffs, two risks worth surfacing. Keep it under 200 words.' },
          { type: 'reflect', body: 'Run the brief past the meeting goal: would skipping the meeting cost you anything the brief did not already cover?' },
        ],
      },
      {
        slug: 'recap-and-actions',
        title: 'Recap and actions in five minutes',
        duration: 7,
        summary: 'A reliable recap shape so decisions stop slipping between meetings.',
        content: [
          { type: 'concept', body: 'A useful recap is short and structurally identical every time: decisions, owners, dates, open questions. Identical structure makes it scannable.' },
          { type: 'do', body: 'Paste the transcript or your messy notes. Ask the model to extract decisions, owners, dates, and open questions only. Reject any text that does not fit those four buckets.', prompt: 'Extract from these notes: decisions made, owners and due dates, open questions. Four sections, no other text.' },
          { type: 'reflect', body: 'Where did the model invent an owner or a date that was not actually agreed? That gap is where your meetings need to be more explicit.' },
        ],
      },
    ],
  },
  {
    slug: 'decisions-and-memos',
    title: 'Decisions and memos people will read',
    description: 'Use AI to make a decision visible — to yourself first, then to the team.',
    output: 'A short decision memo and a habit for using one.',
    lessons: [
      {
        slug: 'one-page-decision-memo',
        title: 'A one-page decision memo',
        duration: 9,
        summary: 'A simple shape that forces a decision into focus before you ask anyone to weigh in.',
        content: [
          { type: 'concept', body: 'Memo shape: context, options, recommendation, risks, ask. Five sections, one page. The discipline is the value.' },
          { type: 'do', body: 'Pick a real decision you owe someone. Have AI draft each section from your raw notes. Then rewrite it in your own voice — the model is the scaffold, not the author.', prompt: 'Draft a one-page decision memo with: Context, Options (3 max), Recommendation, Risks, Ask. Use my notes below. Keep each section under 100 words.' },
          { type: 'reflect', body: 'After rewriting, check: is the recommendation actually defensible if the team picks the second option? If not, the analysis is weak, not the memo.' },
        ],
      },
      {
        slug: 'pre-mortem',
        title: 'Run a pre-mortem with AI',
        duration: 8,
        summary: 'Surface the failure modes before you ship — by asking the model to play the post-mortem first.',
        content: [
          { type: 'concept', body: 'A pre-mortem flips the script: imagine the project failed in six months, then explain why. The exercise pulls risks out of hiding.' },
          { type: 'do', body: 'Paste your plan. Ask: "It is six months from now and this failed. List the five most likely reasons in order, with how each would have been visible early." Treat the answer as a checklist, not a prediction.', prompt: 'Imagine this project failed in six months. List the five most likely reasons in order. For each, describe an early signal we could have caught.' },
        ],
      },
    ],
  },
  {
    slug: 'one-on-ones-and-feedback',
    title: 'One-on-ones and feedback that land',
    description: 'Use AI as a sparring partner for the conversations that actually move people.',
    output: 'A practice routine for harder conversations.',
    lessons: [
      {
        slug: 'rehearse-the-hard-talk',
        title: 'Rehearse the hard conversation',
        duration: 8,
        summary: 'Run the conversation against an AI that pushes back the way the real person might.',
        content: [
          { type: 'concept', body: 'Rehearsal is not about memorising lines. It is about hearing your own framing out loud and noticing where it collapses.' },
          { type: 'do', body: 'Describe the conversation and the other person to the model. Ask it to play them, push back honestly, and stop when you say "freeze" so you can revise.', prompt: 'Play [name], a [role] who tends to [tendency]. I am about to give them feedback on [topic]. Push back honestly. Pause when I say freeze.' },
          { type: 'reflect', body: 'Where did the rehearsal go off the rails? That is the part of your framing the real conversation will catch you on.' },
        ],
      },
      {
        slug: 'written-feedback',
        title: 'Written feedback that does not curdle',
        duration: 7,
        summary: 'A reliable shape for feedback in writing — specific, kind, and actionable.',
        content: [
          { type: 'concept', body: 'Three parts: a specific moment, the impact, the change you want. AI can help you check for vague claims and unspoken assumptions.' },
          { type: 'do', body: 'Draft your feedback. Paste it and ask the model to flag any sentence that is vague, accusatory, or assumes intent. Rewrite from there.', prompt: 'Read this feedback I am about to send. Flag any sentence that is vague, accusatory, or assumes intent. Suggest a tighter version of each.' },
        ],
      },
    ],
  },
];

const jobsModules: Module[] = [
  {
    slug: 'resume-and-story',
    title: 'Your resume and your story',
    description: 'Get your story out of your head and onto the page in a shape recruiters actually read.',
    output: 'A resume that reads like a person, not a job description.',
    lessons: [
      {
        slug: 'resume-rewrite',
        title: 'Rewrite a bullet from impact, not duty',
        duration: 8,
        summary: 'Most resume bullets describe the job description. The good ones describe what changed because you were there.',
        content: [
          { type: 'concept', body: 'A useful bullet has three parts: what you did, the constraint you faced, the measurable change. Without those, the bullet is just a job posting.' },
          { type: 'do', body: 'Paste a current bullet. Ask the model to rewrite it three ways with a measurable change in each. If you cannot put a number on it, write the qualitative outcome instead — "shipped before Q3 deadline" beats "responsible for shipping."', prompt: 'Rewrite this resume bullet three ways. Each must include the action, the constraint, and a measurable or concrete change. Keep each under 25 words.' },
          { type: 'reflect', body: 'Which version makes you sound like a doer, not a watcher? Save it.' },
        ],
      },
      {
        slug: 'story-of-your-work',
        title: 'The 90-second story of your work',
        duration: 8,
        summary: 'A short, repeatable story you can tell at the start of any interview without scrambling.',
        content: [
          { type: 'concept', body: 'Three beats: where you started, what you optimised for, where that took you. Same structure every time so delivery gets clean.' },
          { type: 'do', body: 'Tell the story to AI. Ask it to play a curious recruiter and ask three follow-up questions. Notice which ones make you stumble — those are the soft spots in your story.', prompt: 'I am going to tell you the 90-second story of my career. Play a curious senior recruiter and ask three sharp follow-up questions afterwards.' },
        ],
      },
    ],
  },
  {
    slug: 'networking-and-outreach',
    title: 'Networking without the cringe',
    description: 'Cold outreach and warm reconnections that read like a person wrote them.',
    output: 'A small playbook of outreach moves you will actually send.',
    lessons: [
      {
        slug: 'reconnect-with-grace',
        title: 'Reconnect with someone you have not talked to in years',
        duration: 7,
        summary: 'A short message that is honest about the gap and clear about the ask.',
        content: [
          { type: 'concept', body: 'A good reconnection note acknowledges the gap, says something specific about why this person, and ends with a small, concrete ask.' },
          { type: 'do', body: 'Paste what you remember about the relationship. Ask the model for three drafts at different temperatures: warm, neutral, professional. Pick the one that sounds like you.', prompt: 'Draft three short reconnection messages to [person]. Acknowledge the gap honestly. Make a specific, small ask. Vary the tone: warm, neutral, professional.' },
        ],
      },
      {
        slug: 'cold-outreach-that-works',
        title: 'Cold outreach that does not feel cold',
        duration: 8,
        summary: 'A four-line message structure for asking strangers for fifteen minutes of their time.',
        content: [
          { type: 'concept', body: 'Four lines: who you are, why this person specifically, the fifteen-minute ask, an out. Anything longer reads like a sales email.' },
          { type: 'do', body: 'Pick a real target. Paste their public bio. Ask AI for three drafts that follow the four-line shape, each anchored to a different part of their bio.', prompt: 'Read this person\'s bio. Draft three four-line cold messages. Each must reference a specific part of their bio, ask for fifteen minutes, and offer an easy out. Under 80 words each.' },
        ],
      },
    ],
  },
  {
    slug: 'interview-and-offer',
    title: 'Interviews and the offer conversation',
    description: 'Run mock interviews with a tough sparring partner and walk into the offer conversation prepared.',
    output: 'A practice routine and an offer-conversation script.',
    lessons: [
      {
        slug: 'tough-mock-interview',
        title: 'A mock interview that actually challenges you',
        duration: 10,
        summary: 'Most mock interviews are too kind. The model can be honest in a way friends will not.',
        content: [
          { type: 'concept', body: 'Tell the model the role, the company, and the kind of interview. Ask for the four hardest questions you are likely to fumble.' },
          { type: 'do', body: 'Run the mock. After each answer, ask for one specific critique: vague claim, weak example, dodged question. Iterate until the answer is tight.', prompt: 'You are a senior interviewer for a [role] at a [company-size] [industry] company. Ask me the four hardest questions for this role. After each answer, give one specific critique.' },
        ],
      },
      {
        slug: 'offer-conversation',
        title: 'The offer conversation, calmly',
        duration: 8,
        summary: 'Negotiate without the panic by rehearsing the moves first.',
        content: [
          { type: 'concept', body: 'Three moves: thank-you and excitement, the specific ask with reasoning, the close. Same shape every time so nerves do not derail you.' },
          { type: 'do', body: 'Paste the offer details. Ask AI to play a thoughtful recruiter. Run the conversation twice — once where they push back, once where they say yes. Notice your reactions.', prompt: 'You are a thoughtful recruiter at [company]. I have an offer of [terms] and want to ask for [ask]. Play it twice: once you push back, once you agree. I will react.' },
        ],
      },
    ],
  },
];

const agentsModules: Module[] = [
  {
    slug: 'what-agents-are',
    title: 'What makes something an agent',
    description: 'A clear mental model for the difference between a chatbot response and an agentic loop.',
    output: 'A working definition of what agents are and are not.',
    lessons: [
      {
        slug: 'beyond-the-chatbox',
        title: 'Beyond the chatbox',
        duration: 8,
        summary: 'A chatbot answers once. An agent acts, checks the result, and acts again.',
        content: [
          { type: 'concept', title: 'One response vs. a loop', body: 'A standard AI session is linear: you send a message, the model replies. An agent is a loop: the model plans a step, takes an action (browsing the web, running code, reading a file), checks the result, then decides the next step. The same model — fundamentally different shape.' },
          { type: 'concept', title: 'What "acting" means', body: 'Agents have tools: the ability to search the web, run code, write to files, call an API, open a browser. Without tools, a model can only respond. With tools, it can do. The tools are what make the loop possible.' },
          { type: 'example', body: 'You ask a chatbot: "Find the cheapest flight from New York to Tokyo next Thursday." It describes what you should do. You ask an agent the same question: it searches travel sites, compares results, and returns specific options with current prices.' },
          { type: 'reflect', body: 'Think of a task you have asked an AI to help with this month. Was the output purely text — or did it require the AI to actually go do something? That distinction is the line between chatbot and agent.' },
        ],
      },
      {
        slug: 'the-agentic-loop',
        title: 'The agentic loop: plan, act, observe, revise',
        duration: 9,
        summary: 'The four-step cycle at the core of every agent, and why it sometimes goes wrong.',
        content: [
          { type: 'concept', title: 'The loop in plain English', body: 'Every agent runs the same cycle: 1) plan what to do next, 2) use a tool to act, 3) observe what came back, 4) decide whether the task is done or needs another step. Then repeat. Understanding the loop is what lets you anticipate where things go quietly wrong.' },
          { type: 'concept', title: 'Why the loop matters to you', body: 'Each step is a chance for error to compound. A wrong plan leads to a wrong action leads to a misleading observation. By the time you see the final output, three silent mistakes may have shaped it — and the model will not flag them unless you look at the trace.' },
          { type: 'example', body: 'An agent asked to "summarize everything on this company\'s blog" might plan: fetch the blog index → extract post URLs → read each post → write a summary. If step one returns a JavaScript-rendered page the agent cannot read, it may silently skip posts and present a partial summary as complete.' },
          { type: 'reflect', body: 'Of the four steps — plan, act, observe, revise — where do you think most silent failures happen? Think of one task you would trust an agent with. Which step would you watch most closely?' },
        ],
      },
    ],
  },
  {
    slug: 'using-agents-today',
    title: 'Using agent mode today',
    description: 'How to give agents work that succeeds, and how to read the output before you trust it.',
    output: 'A reliable habit for tasking and verifying agentic work.',
    lessons: [
      {
        slug: 'agent-mode-controls',
        title: 'Agent mode controls — what they mean',
        duration: 8,
        summary: 'A plain-language tour of the settings and permissions in agent and Projects interfaces.',
        content: [
          { type: 'concept', body: 'Agent mode lets you attach tools (web search, code execution, file reading) and give the model permission to use them autonomously. The key setting is the trust boundary: how many steps can it take before checking back with you? Higher autonomy means fewer interruptions but more opportunity for quiet mistakes.' },
          { type: 'concept', title: 'Projects vs. one-off agents', body: 'Claude Projects give an agent persistent context: a system prompt, uploaded files, and remembered state across sessions. A one-off agent starts fresh. Use Projects when you need the agent to know your conventions and history. Use a fresh session when you want a clean, auditable run.' },
          { type: 'do', body: 'Open an agent or Projects interface and review the available tools. For each tool, ask yourself: if the model used this without checking back, what is the worst realistic outcome? That risk assessment is your guide to which tools to enable for which tasks.' },
          { type: 'reflect', body: 'Which tool permission makes you most uncomfortable to hand to an autonomous agent? That discomfort is usually pointing at a task boundary worth keeping for yourself.' },
        ],
      },
      {
        slug: 'tasking-an-agent',
        title: 'Giving an agent a task that actually works',
        duration: 10,
        summary: 'The four parts of a good task description: goal, constraints, stopping condition, and output format.',
        content: [
          { type: 'concept', body: 'Most agentic failures trace back to an underspecified task. The model fills gaps with assumptions, and assumptions compound across a multi-step loop. Four elements cut most of the ambiguity: what you want (goal), what to avoid (constraints), when to stop (stopping condition), and what to give you (output format).' },
          { type: 'example', body: 'Weak: "Research our competitors and write a summary." Strong: "Search for the three companies most often compared to Acme in SaaS forums. For each, find their pricing page and current feature list. Stop at three companies. Return a table with columns: company name, price, top three features, one differentiator."' },
          { type: 'do', body: 'Write an agent task you would actually give. Then review it: does it have a goal, constraints, a stopping condition, and an output format? Rewrite the weakest part.', prompt: 'Here is a task I want to give an AI agent: [paste your task]. Identify which of the four elements — goal, constraints, stopping condition, output format — is weakest or missing. Rewrite that element to be more specific.' },
          { type: 'reflect', body: 'What is the difference between a goal and a stopping condition? Where might an agent that has the goal but not the stopping condition cause a problem?' },
        ],
      },
      {
        slug: 'reading-the-work-log',
        title: "Reading the work log before you trust the output",
        duration: 8,
        summary: 'The three things to check in a tool-use trace before using the result.',
        content: [
          { type: 'concept', body: 'Most agentic interfaces show a trace: a record of what the agent planned, which tools it called, and what each returned. Most users skip straight to the output. This is how silent failures become real mistakes.' },
          { type: 'concept', title: 'Three things to check', body: '1) Did the agent use the tools it was supposed to, in a sensible order? 2) Were there any tool errors it silently recovered from — or quietly worked around? 3) Does the conclusion actually follow from the observations, or does it go further than the evidence supports?' },
          { type: 'do', body: 'Run a real agentic task. When it finishes, open the tool trace before reading the output. Find one moment where the agent made a judgment call not explicitly in your instructions. Was it the right call? What would have happened if it was wrong?' },
        ],
      },
    ],
  },
  {
    slug: 'limits-and-judgment',
    title: 'Limits, failures, and judgment',
    description: 'Where agents break, when chaining is worth the overhead, and when to skip the agent entirely.',
    output: 'A calibrated sense of when and how to use agentic AI.',
    lessons: [
      {
        slug: 'where-agents-break',
        title: 'Where agents break and why',
        duration: 9,
        summary: 'Four failure modes that catch people off guard: error compounding, tool hallucination, context loss, and confident incompleteness.',
        content: [
          { type: 'concept', title: 'Error compounding', body: 'In a multi-step loop, a small early error shapes every later step. A web search that returns one bad result can anchor the entire downstream analysis. Unlike a single prompt, there is no clean moment where the model reconsiders from scratch.' },
          { type: 'concept', title: 'Tool hallucination and silent failure', body: 'Models sometimes generate plausible-looking tool calls that fail silently, or produce outputs the model then fabricates continuations for. The model does not always know when its tools have failed. Checking the trace is the only reliable fix.' },
          { type: 'concept', title: 'Confident incompleteness', body: 'An agent that could not complete a step will often present its partial result as if complete. It did not lie — it presented what it had. You asked for a summary of all ten blog posts; it read six. The output looks right. The scope was wrong.' },
          { type: 'reflect', body: 'Of the three failure modes — error compounding, tool hallucination, confident incompleteness — which one would be hardest to detect in an output you received without seeing the trace? Why?' },
        ],
      },
      {
        slug: 'chaining-vs-agents',
        title: 'Manual chaining vs. an autonomous agent',
        duration: 8,
        summary: 'When a sequence of single prompts is better than an autonomous loop — and when it is not.',
        content: [
          { type: 'concept', body: 'A chained prompt sequence is when you manually run step one, review it, then run step two with the output. Slower, but every step is audited by you. A real agent runs the whole loop autonomously. Faster, but you only see the start and the end.' },
          { type: 'concept', body: 'Use manual chaining when the stakes are high, when the steps require your judgment before continuing, or when the task is short enough to manage yourself. Use an agent when the steps are many, each is low-stakes, and speed matters more than auditability.' },
          { type: 'do', body: 'Pick a multi-step task you do regularly that involves AI. Which steps require your judgment before the next step can proceed? Everything before your first judgment call is a candidate for an agent. Everything after is where you take back control.', prompt: 'I do this multi-step task regularly: [describe task]. Identify which steps require human judgment before the next step can proceed. Suggest a split: what an agent should handle autonomously, and where I should take back control.' },
        ],
      },
      {
        slug: 'when-not-to-use-an-agent',
        title: 'When not to use an agent',
        duration: 7,
        summary: 'A short decision framework for tasks that are better served by a well-written single prompt.',
        content: [
          { type: 'concept', body: 'Agents add overhead: more tokens, more time, more failure surface. For tasks where a single well-structured prompt returns a usable answer in one shot, an agent is strictly worse. The machinery solves a problem that does not exist.' },
          { type: 'concept', title: 'The two questions', body: 'Before reaching for an agent, ask: 1) Does this task require information the model cannot have in a single context window? 2) Does this task require taking actions in the world — searching, writing to a file, calling an API? If neither is yes, a prompt is the better tool.' },
          { type: 'do', body: 'Take three tasks you have used or considered using an agent for. Run them through the two questions. Which ones genuinely need an agent — and which ones were just habit?', prompt: 'For each of these tasks, tell me whether an AI agent adds real value or whether a well-written single prompt would work just as well. Explain the reasoning for each.\n\nTasks:\n[list your tasks]' },
          { type: 'reflect', body: 'The most skilled users of agentic AI are not the ones who use it most — they are the ones who know exactly when not to. What is one task you have been agent-ing that you should probably just prompt instead?' },
        ],
      },
    ],
  },
];

export const skillPaths: SkillPath[] = [
  {
    slug: 'beginners',
    title: 'AI for Beginners',
    tagline: 'Your first 30 days with AI, without the jargon.',
    description: 'No prior experience assumed. Build a clear mental model of what AI can and cannot do, learn to spot mistakes, and finish your first useful task.',
    difficulty: 'Beginner',
    totalMinutes: 60,
    outcomes: [
      'Understand what AI actually does in plain language',
      'Spot a hallucination before it spots you',
      'Use AI to finish one real task you have been putting off',
    ],
    glyph: 'sapling',
    accent: 'from-accent-400 to-accent-600',
    modules: beginnersModules,
  },
  {
    slug: 'learners',
    title: 'AI for Students & Learners',
    tagline: 'Learn faster, study smarter, stay honest.',
    description: 'For anyone in school or self-teaching. Use AI as a tutor, summarizer, and sparring partner without short-circuiting the learning itself.',
    difficulty: 'Beginner',
    totalMinutes: 75,
    outcomes: [
      'Use AI as a tutor without losing the thinking',
      'Turn dense reading into real notes',
      'Practice the questions that catch you off guard',
    ],
    glyph: 'page',
    accent: 'from-accent-500 to-accent-700',
    modules: learnersModules,
  },
  {
    slug: 'parents',
    title: 'AI for Parents',
    tagline: 'Coach your kid through AI with confidence.',
    description: 'A companion to PromptGPT Kids. Learn what to look for on the guardian dashboard, the right questions to ask, and a healthy weekly rhythm.',
    difficulty: 'Beginner',
    totalMinutes: 50,
    outcomes: [
      'Have the five conversations every parent should have',
      'Read a transcript well — what to notice, what to ignore',
      'Set a weekly rhythm that builds trust, not surveillance',
    ],
    glyph: 'arcs',
    accent: 'from-rust-500 to-rust-700',
    modules: parentsModules,
  },
  {
    slug: 'daily',
    title: 'AI for Daily Life',
    tagline: 'Small AI moves that take friction out of a normal week.',
    description: 'Practical patterns for inbox, planning, and the small decisions that pile up. Nothing fancy — just the moves you will actually reuse.',
    difficulty: 'Beginner',
    totalMinutes: 70,
    outcomes: [
      'Get through the hard email in five minutes',
      'Plan a week of meals from what you already have',
      'Use AI to think more clearly, not just faster',
    ],
    glyph: 'horizon',
    accent: 'from-ink-600 to-ink-800',
    modules: dailyModules,
  },
  {
    slug: 'creative',
    title: 'AI for Creative Work',
    tagline: 'Brainstorm wider, write sharper, defend your taste.',
    description: 'For writers, makers, and hobbyists. Use AI for quantity and pressure-testing while keeping your own judgement at the center.',
    difficulty: 'Intermediate',
    totalMinutes: 80,
    outcomes: [
      'Generate variety without diluting your taste',
      'Rewrite a headline ten ways and pick the winner',
      'Have AI attack your idea before the world does',
    ],
    glyph: 'spiral',
    accent: 'from-accent-600 to-ink-800',
    modules: creativeModules,
  },
  {
    slug: 'work',
    title: 'AI for Work',
    tagline: 'The Monday-morning patterns: meetings, memos, decisions, feedback.',
    description: 'For knowledge workers who want AI to make their week tighter — better prep, sharper decisions, and feedback that lands. Pro library.',
    difficulty: 'Intermediate',
    totalMinutes: 60,
    outcomes: [
      'Walk into meetings with a two-minute brief that sharpens the room',
      'Write decision memos that hold up when the team picks the second option',
      'Rehearse hard conversations with a sparring partner who pushes back',
    ],
    glyph: 'frame',
    accent: 'from-ink-700 to-accent-700',
    modules: workModules,
    proOnly: true,
  },
  {
    slug: 'jobs',
    title: 'AI for Job Seekers',
    tagline: 'Resume, networking, interview, offer — the whole arc.',
    description: 'For anyone in a search. Use AI to get your story onto the page, send outreach that does not feel cold, and rehearse the rooms before you walk in. Pro library.',
    difficulty: 'Beginner',
    totalMinutes: 60,
    outcomes: [
      'Rewrite resume bullets that read like impact, not duties',
      'Send outreach that real people actually reply to',
      'Run mock interviews tough enough to be useful',
    ],
    glyph: 'path',
    accent: 'from-accent-500 to-rust-700',
    modules: jobsModules,
    proOnly: true,
  },
  {
    slug: 'agents',
    title: 'AI Agents',
    tagline: 'From chatbox to autonomous loop — how agents work and when to use them.',
    description: 'A clear, practical guide to agentic AI. Understand the plan-act-observe-revise loop, write tasks that succeed, read work traces, and know exactly when an agent is the wrong tool.',
    difficulty: 'Intermediate',
    totalMinutes: 75,
    outcomes: [
      'Understand the loop that drives every agent — and where it quietly fails',
      'Write task descriptions with goals, constraints, stopping conditions, and output formats',
      'Read a work trace and catch silent mistakes before they reach you',
      'Know when an agent adds real value and when a single prompt is better',
    ],
    glyph: 'hook',
    accent: 'from-ink-600 to-accent-700',
    modules: agentsModules,
    proOnly: true,
  },
];

export const skillPathBySlug = (slug: string) => skillPaths.find((p) => p.slug === slug);
export const lessonByPathAndSlug = (pathSlug: string, lessonSlug: string) => {
  const path = skillPathBySlug(pathSlug);
  if (!path) return null;
  for (const m of path.modules) {
    const l = m.lessons.find((x) => x.slug === lessonSlug);
    if (l) return { path, module: m, lesson: l };
  }
  return null;
};

export const allLessons = () => skillPaths.flatMap((p) => p.modules.flatMap((m) => m.lessons.map((l) => ({ path: p, module: m, lesson: l }))));

export const skillDimensions = [
  { key: 'prompting', label: 'Prompting', glyph: 'prompt' as GlyphName },
  { key: 'research', label: 'Research', glyph: 'page' as GlyphName },
  { key: 'analysis', label: 'Analysis', glyph: 'mirror' as GlyphName },
  { key: 'content', label: 'Content', glyph: 'spiral' as GlyphName },
  { key: 'planning', label: 'Planning', glyph: 'path' as GlyphName },
] as const;

export const navigationIcons = { BookOpen, Target, Users, FileText, Briefcase, Inbox, ListChecks, MessagesSquare, Zap };
